import { addDays, format, parseISO, startOfDay } from 'date-fns';
import type { ScheduleResource, ScheduleTask, ScheduleVehicle } from '../../../services/scheduleService';

export const normalizeVehicleType = (v: Pick<ScheduleVehicle, 'vehicleType'>): string =>
  (v.vehicleType || 'Unspecified').trim() || 'Unspecified';

/** Vehicles linked to the foreman area's service area (schedule vehicle rows include all SA links). */
export function filterVehiclesForPlanScope(
  vehicles: ScheduleVehicle[],
  serviceAreaId: number | null
): ScheduleVehicle[] {
  if (serviceAreaId == null) return vehicles;
  return vehicles.filter((v) => (v.serviceAreas || []).some((sa) => sa.id === serviceAreaId));
}

export function buildVehicleTypeOptions(vehicles: ScheduleVehicle[]): Array<{ type: string; capacity: number }> {
  const map = new Map<string, number>();
  for (const v of vehicles) {
    const t = normalizeVehicleType(v);
    map.set(t, (map.get(t) || 0) + 1);
  }
  return Array.from(map.entries())
    .map(([type, capacity]) => ({ type, capacity }))
    .sort((a, b) => a.type.localeCompare(b.type));
}

export function buildUnitToTypeMap(vehicles: ScheduleVehicle[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const v of vehicles) {
    const u = (v.unitNumber || '').trim();
    if (!u) continue;
    m.set(u, normalizeVehicleType(v));
  }
  return m;
}

export function inferUniqueTypesFromUnits(unitNumbers: string[], vehicles: ScheduleVehicle[]): string[] {
  const map = buildUnitToTypeMap(vehicles);
  const types = new Set<string>();
  for (const u of unitNumbers) {
    const unit = (u || '').trim();
    if (!unit) continue;
    types.add(map.get(unit) || 'Unspecified');
  }
  return Array.from(types);
}

/** One representative unit per selected type (alphabetically first), for persisting `assignedVehicleUnitNumbers`. */
export function pickRepresentativeUnitsForTypes(
  types: string[],
  vehicles: ScheduleVehicle[],
  serviceAreaId: number | null
): string[] {
  const scoped = filterVehiclesForPlanScope(vehicles, serviceAreaId);
  const out: string[] = [];
  for (const t of types) {
    const want = (t || '').trim() || 'Unspecified';
    const candidates = scoped.filter((v) => normalizeVehicleType(v) === want);
    const sorted = [...candidates].sort((a, b) => a.unitNumber.localeCompare(b.unitNumber));
    if (sorted.length > 0) {
      out.push(sorted[0].unitNumber);
    }
  }
  return out;
}

function parseTaskDayRange(task: ScheduleTask): { start: Date; end: Date } | null {
  const idRaw = task.identifiedDate ?? task.startDate;
  if (!idRaw) return null;
  const startStr = typeof idRaw === 'string' ? idRaw.split('T')[0] : String(idRaw);
  const endRaw = task.plannedCompletionDate;
  if (!endRaw) return null;
  const endStr = typeof endRaw === 'string' ? endRaw.split('T')[0] : String(endRaw);
  const start = startOfDay(parseISO(startStr));
  const end = startOfDay(parseISO(endStr));
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  return { start, end };
}

function eachDayInclusive(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, 1)) {
    days.push(d);
  }
  return days;
}

/**
 * Counts assigned vehicle units per type per calendar day (overlap with [rangeStart, rangeEnd]),
 * for tasks in the same foreman area. Excludes the given task id when editing.
 */
export function countVehicleTypeDemandByDay(
  resources: ScheduleResource[],
  unitToType: Map<string, string>,
  rangeStart: Date,
  rangeEnd: Date,
  foremanAreaId: number | null,
  excludeTaskId?: number
): Map<string, Map<string, number>> {
  const result = new Map<string, Map<string, number>>();
  const add = (dayKey: string, type: string, delta: number) => {
    if (!result.has(dayKey)) result.set(dayKey, new Map());
    const m = result.get(dayKey)!;
    m.set(type, (m.get(type) || 0) + delta);
  };

  for (const res of resources) {
    for (const task of res.tasks || []) {
      if (excludeTaskId != null && task.id === excludeTaskId) continue;
      if (foremanAreaId != null && task.foremanAreaId !== foremanAreaId) continue;
      const tr = parseTaskDayRange(task);
      if (!tr) continue;
      const overlapStart = new Date(Math.max(tr.start.getTime(), rangeStart.getTime()));
      const overlapEnd = new Date(Math.min(tr.end.getTime(), rangeEnd.getTime()));
      if (overlapEnd.getTime() < overlapStart.getTime()) continue;

      for (const veh of task.vehicles || []) {
        const u = (veh.unitUsed || '').trim();
        if (!u) continue;
        const t = unitToType.get(u) || 'Unspecified';
        for (const d of eachDayInclusive(overlapStart, overlapEnd)) {
          add(format(d, 'yyyy-MM-dd'), t, 1);
        }
      }
    }
  }
  return result;
}

export function isVehicleTypeSelectionOverCapacity(
  selectedTypes: string[],
  capacityByType: Map<string, number>,
  demandByDay: Map<string, Map<string, number>>,
  taskRangeStart: Date,
  taskRangeEnd: Date
): boolean {
  const days = eachDayInclusive(taskRangeStart, taskRangeEnd);
  for (const type of selectedTypes) {
    const t = (type || '').trim() || 'Unspecified';
    const cap = capacityByType.get(t) || 0;
    if (cap <= 0) return true;
    for (const d of days) {
      const key = format(d, 'yyyy-MM-dd');
      const used = demandByDay.get(key)?.get(t) || 0;
      if (used + 1 > cap) return true;
    }
  }
  return false;
}
